/**
 * Publish · Unpublish · Coming soon — the three decisions that are not part of the form.
 *
 * They are separated from Save on purpose. Everything else on the page is copy an admin can
 * revise all afternoon with nobody noticing; these two flags are the only edits a parent feels
 * the moment they land, so each one is a switch with a confirm that **states the consequence**
 * rather than asking "are you sure?".
 *
 * The consequence has two halves, and both are true: the Studio's picker reads the catalog
 * live and changes at once; the marketing site renders it at build time and does not change
 * until it is rebuilt. Saying only the first would be a half-truth an admin would find out
 * about from someone else.
 *
 * Publishing is refused while `validateMedium` has anything to say. Unpublishing never is —
 * taking something off a shelf must not be blocked by the reason it should come off.
 *
 * The gate only runs on the way in, though, so a row that went up before it existed can be on
 * the shelf without a price. `attentionReasons` asks the same question of a stored record, and
 * the answer sits above the buttons here — the same words the catalog list shows in its state
 * column, said where the fix is.
 */

import { Button, Dialog, StatusBadge } from '@atlas-nook/design-system';
import { attentionReasons, publishBlockers } from '@atlas-nook/mediums';
import type { Medium } from '@atlas-nook/mediums';
import { useState } from 'react';
import type { JSX } from 'react';

import { setMediumAvailabilityFlags } from '../data/writes.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';

type Ask = 'publish' | 'unpublish' | 'coming-soon' | 'not-coming-soon';

interface Consequence {
  title: string;
  body: string;
  confirm: string;
  flags: { published?: boolean; comingSoon?: boolean };
  danger?: boolean;
}

function consequenceOf(ask: Ask, name: string): Consequence {
  switch (ask) {
    case 'publish':
      return {
        title: S.medium.publishTitle(name),
        body: S.medium.publishBody,
        confirm: S.medium.publishConfirm,
        // Publishing something teased ends the tease: the two states are exclusive.
        flags: { published: true, comingSoon: false },
      };
    case 'unpublish':
      return {
        title: S.medium.unpublishTitle(name),
        body: S.medium.unpublishBody,
        confirm: S.medium.unpublishConfirm,
        flags: { published: false },
        danger: true,
      };
    case 'coming-soon':
      return {
        title: S.medium.comingSoonTitle(name),
        body: S.medium.comingSoonBody,
        confirm: S.medium.comingSoonConfirm,
        flags: { comingSoon: true },
      };
    case 'not-coming-soon':
      return {
        title: S.medium.notComingSoonTitle(name),
        body: S.medium.notComingSoonBody,
        confirm: S.medium.notComingSoonConfirm,
        flags: { comingSoon: false },
      };
  }
}

export function PublishControls({
  medium,
  blocked,
}: {
  medium: Medium;
  /** True while `validateMedium` has problems. Publishing waits; unpublishing never does. */
  blocked: boolean;
}): JSX.Element {
  const toast = useToast();
  const [ask, setAsk] = useState<Ask | null>(null);
  const [working, setWorking] = useState(false);

  const name = medium.name.en === '' ? medium.id : medium.name.en;

  /*
   * Published means sold (PRD 03 §4.4), so a row cannot reach the shelf without the two values
   * the store and the Studio display. Same rule as `publishBlockers` everywhere else — the
   * form does not restate it, it asks.
   */
  const missing = publishBlockers(medium);
  /*
   * The same question asked of a row that is already on the shelf. The gate only ever ran on
   * the way in, so a row published before it existed can be sitting there without the price
   * the store is meant to display — and the catalog list now says so in its state column. It
   * has to say so here too, where the fix is.
   */
  const onShelfNeeds = attentionReasons(medium);
  const teased = medium.comingSoon === true;
  const consequence = ask === null ? null : consequenceOf(ask, name);

  async function apply(): Promise<void> {
    if (consequence === null) return;
    setWorking(true);
    try {
      await setMediumAvailabilityFlags(medium.id, consequence.flags);
      setAsk(null);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="pubrow">
      {onShelfNeeds.length > 0 && (
        <p className="cap pubneeds">{S.medium.onShelfNeeds(onShelfNeeds)}</p>
      )}

      <StatusBadge status={medium.published ? 'ready' : teased ? 'needs-ok' : 'none'}>
        {medium.published
          ? S.catalog.state.published
          : teased
            ? S.catalog.state.comingSoon
            : S.catalog.state.hidden}
      </StatusBadge>

      <div className="btnrow">
        {medium.published ? (
          <Button tone="quiet" size="sm" onClick={() => setAsk('unpublish')}>
            {S.medium.unpublish}
          </Button>
        ) : (
          <Button
            tone="secondary"
            size="sm"
            isDisabled={blocked || missing.length > 0}
            onClick={() => setAsk('publish')}
          >
            {S.medium.publish}
          </Button>
        )}

        {!medium.published &&
          (teased ? (
            <Button tone="quiet" size="sm" onClick={() => setAsk('not-coming-soon')}>
              {S.medium.notComingSoon}
            </Button>
          ) : (
            <Button tone="quiet" size="sm" onClick={() => setAsk('coming-soon')}>
              {S.medium.comingSoon}
            </Button>
          ))}
      </div>

      {blocked && !medium.published && <span className="cap">{S.medium.blockedByProblems}</span>}
      {!blocked && !medium.published && missing.length > 0 && (
        <span className="cap">
          {S.medium.publishNeeds(missing.map((field) => S.medium[field]).join(' · '))}
        </span>
      )}

      <Dialog
        open={consequence !== null}
        onClose={() => setAsk(null)}
        title={consequence?.title ?? ''}
        tone={consequence?.danger === true ? 'danger' : 'standard'}
        actions={
          <>
            <Button tone="quiet" onClick={() => setAsk(null)}>
              {S.common.cancel}
            </Button>
            <Button
              tone={consequence?.danger === true ? 'danger' : 'primary'}
              working={working}
              onClick={() => void apply()}
            >
              {consequence?.confirm ?? ''}
            </Button>
          </>
        }
      >
        <p className="note">{consequence?.body}</p>
      </Dialog>
    </div>
  );
}
